import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6ffnkbe24agvuu3j50";

type Status = "idle" | "loading" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setErrorMsg("");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg ?? "").toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，请稍后再试。");
    }
  };

  const inputClass =
    "w-full text-sm px-4 py-3 rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full rounded-xl bg-background-50 border border-background-200 p-6 md:p-8"
    >
      <h2 className="text-lg font-bold text-foreground-950 mb-1.5">意见反馈</h2>
      <p className="text-sm text-foreground-500 mb-6">
        有任何建议或想看的影视内容，欢迎告诉我们。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-700 mb-2">
            称呼
          </label>
          <input id="fb-name" name="name" type="text" required placeholder="请输入你的称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-700 mb-2">
            邮箱
          </label>
          <input id="fb-email" name="email" type="email" required placeholder="name@example.com" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-subject" className="block text-sm font-medium text-foreground-700 mb-2">
          反馈类型
        </label>
        <select id="fb-subject" name="subject" defaultValue="内容建议" className={inputClass}>
          <option value="内容建议">内容建议</option>
          <option value="影视推荐">影视推荐</option>
          <option value="功能反馈">功能反馈</option>
          <option value="合作洽谈">合作洽谈</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-700 mb-2">
          留言内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          rows={5}
          required
          maxLength={500}
          placeholder="请填写你的建议（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
      </div>

      <input
        type="text"
        name="company_alt"
        className="form-honeypot-guard"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-6 w-full sm:w-auto px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "loading" ? "发送中…" : "提交反馈"}
      </button>

      {status === "success" && (
        <p className="mt-4 text-sm text-primary-700 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line" /> 反馈已成功发送，感谢你的建议！
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 text-sm text-secondary-800 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {errorMsg}
        </p>
      )}
    </form>
  );
}